import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { message, Tooltip } from 'antd';
import { UserAddOutlined, EditOutlined } from '@ant-design/icons';
import { captureAnalytics } from '../../../../actions/preload';
import { saveSenderContact, senderContactSaved } from '../../../../actions/senderContacts';
import { validFaxNumber } from '../../../../global/validation';
import SenderContactEditor from './SenderContactEditor';
import './senderContact.scss';

export const hasSavedSenderContact = (record) => record.receiver_type === 'received_fax'
  && Boolean(record.sender_contact_details?.contact) && validFaxNumber(record.sender_email).valid;

export const SenderContactAction = ({
  record, surface, organizationId, saveContact, contactSaved, displayName, onEditorOpenChange,
}) => {
  const trigger = useRef(null);
  const [openFor, setOpenFor] = useState(null);
  const details = record.sender_contact_details;
  if (record.receiver_type !== 'received_fax' || !details || !validFaxNumber(record.sender_email).valid) return null;
  const identity = `${organizationId}:${record.sender_contact_details.source_fax_id}`;
  const closeEditor = () => { setOpenFor(null); onEditorOpenChange(false); requestAnimationFrame(() => trigger.current?.focus()); };
  const saved = hasSavedSenderContact(record);
  const label = saved ? 'Edit contact' : 'Save contact';
  const name = displayName || details.contact?.organization_name || details.contact?.contact_name || record.sender_name;
  if (saved && !details.can_edit) return <span className="sender-contact-name">{name}</span>;

  return (
    <span role="presentation" className={`sender-contact${saved ? ' sender-contact--saved' : ''}`} onClick={(event) => event.stopPropagation()} onDoubleClick={(event) => event.stopPropagation()}>
      {details.can_edit ? (
        <button type="button" ref={trigger} className="sender-contact-trigger" aria-label={saved ? `Edit contact: ${name}` : label} onClick={() => { captureAnalytics('sender_contact_editor_opened', { surface, editing: Boolean(details.contact) }); onEditorOpenChange(true); setOpenFor(identity); }}>
          {saved ? (
            <>
              <span>{name}</span>
              <EditOutlined className="sender-contact-pencil" />
            </>
          ) : (
            <>
              <UserAddOutlined />
              <span>{label}</span>
            </>
          )}
        </button>
      ) : <Tooltip title="Only admins can edit contacts"><span className="sender-contact-locked">Contacts managed by admins</span></Tooltip>}
      {openFor === identity && <SenderContactEditor key={identity} record={record} surface={surface} onSave={saveContact} onClose={closeEditor} onSaved={(contact) => { contactSaved(contact, record, organizationId); closeEditor(); message.success('Contact saved'); }} />}
    </span>
  );
};

SenderContactAction.propTypes = {
  onEditorOpenChange: PropTypes.func,
  displayName: PropTypes.string,
  record: PropTypes.object.isRequired,
  surface: PropTypes.string.isRequired,
  organizationId: PropTypes.number,
  saveContact: PropTypes.func.isRequired,
  contactSaved: PropTypes.func.isRequired,
};
SenderContactAction.defaultProps = { organizationId: null, displayName: null, onEditorOpenChange: () => {} };
export default connect((state) => ({ organizationId: state.auth.currentOrganizationId }), { saveContact: saveSenderContact, contactSaved: senderContactSaved })(SenderContactAction);
